import { type RefObject, useEffect } from 'react';

type DefaultOptions = {
    enable?: boolean;
    observeChildren?: boolean;
};

export type Options<T> = DefaultOptions & {
    ref: RefObject<T>;
    childRefs?: RefObject<T[]>;
};

const defaultOptions: DefaultOptions = {
    enable: true,
    observeChildren: false
};

export function useIntersectionEffect<T extends HTMLElement>(
    effectCallback: (entries: IntersectionObserverEntry[]) => void,
    options: Options<T>,
    deps: unknown[] = []
) {
    const { ref, childRefs, enable, observeChildren } = { ...defaultOptions, ...options };

    useEffect(() => {
        const refElement = ref.current;

        if (!enable || !refElement) {
            return;
        }

        const observer = new IntersectionObserver(effectCallback, { root: ref.current, threshold: 1 });

        if (observeChildren) {
            // Observe children to ref
            const { children } = ref.current;
            Array.from(children).forEach((x) => observer.observe(x));
        } else if (childRefs?.current) {
            // Observe childRefs
            childRefs?.current.forEach((x) => observer.observe(x));
        } else {
            // Observe ref
            observer.observe(ref.current);
        }

        return () => {
            observer.disconnect();
        };
    }, deps);
}
